import { useId, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';

/** Buttons und Felder (design.md §7). */

type Variant = 'primary' | 'secondary' | 'quiet' | 'danger' | 'danger-solid';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  /** Läuft die Aktion, ist der Button gesperrt und zeigt `busyText`. */
  busy?: boolean;
  busyText?: string;
  icon?: ReactNode;
}

export function Button({ variant = 'secondary', busy, busyText = 'Wird gespeichert…', icon, children, disabled, className, type, ...rest }: ButtonProps) {
  const cls = variant === 'secondary' ? '' : `btn-${variant}`;
  return (
    <button type={type ?? 'button'} className={[cls, className].filter(Boolean).join(' ') || undefined} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
      {icon}
      {busy ? busyText : children}
    </button>
  );
}

interface FieldShell {
  label: string;
  help?: ReactNode;
  /** Fehlertext sagt, was zu tun ist. */
  error?: string | null;
  wide?: boolean;
}

export function TextField({ label, help, error, wide, id, ...input }: FieldShell & InputHTMLAttributes<HTMLInputElement>) {
  const auto = useId();
  const fieldId = id ?? auto;
  const helpId = `${fieldId}-help`;
  return (
    <div className={['field', error ? 'invalid' : '', wide ? 'wide' : ''].filter(Boolean).join(' ')}>
      <label htmlFor={fieldId}>{label}</label>
      <input id={fieldId} aria-invalid={error ? true : undefined} aria-describedby={error || help ? helpId : undefined} {...input} />
      {(error || help) && (
        <span id={helpId} className="help">
          {error ?? help}
        </span>
      )}
    </div>
  );
}

export function SelectField({
  label,
  help,
  error,
  wide,
  id,
  children,
  ...select
}: FieldShell & SelectHTMLAttributes<HTMLSelectElement> & { children: ReactNode }) {
  const auto = useId();
  const fieldId = id ?? auto;
  return (
    <div className={['field', error ? 'invalid' : '', wide ? 'wide' : ''].filter(Boolean).join(' ')}>
      <label htmlFor={fieldId}>{label}</label>
      <select id={fieldId} {...select}>
        {children}
      </select>
      {(error || help) && <span className="help">{error ?? help}</span>}
    </div>
  );
}
